import React from "react";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { changeBankerPassword } from "../../../services/banker.service";
import Loader from "../../../common/loader";

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current Password is required"),
  newPassword: Yup.string()
    .min(6, "New Password must be at least 6 characters")
    .required("New Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Confirm Password is required"),
});

const PasswordField = ({
  name,
  label,
  show,
  onToggle,
  error,
  touched,
}: {
  name: keyof FormValues;
  label: string;
  show: boolean;
  onToggle: () => void;
  error: boolean;
  touched: string | false | undefined;
}) => (
  <Field
    as={TextField}
    name={name}
    label={label}
    type={show ? "text" : "password"}
    fullWidth
    variant="outlined"
    error={error}
    helperText={touched}
    InputProps={{
      style: { borderRadius: 10 },
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={onToggle}>
            {show ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    }}
  />
);

interface Props {
 bankerId: string;
}
const BankerChangePassword: React.FC<Props> = ({bankerId}) => {
  const [showPassword, setShowPassword] = React.useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [loading, setLoading] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const toggleVisibility = (field: keyof typeof showPassword) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    try {
      setLoading(true);
      await changeBankerPassword(bankerId, values);
      setSnackbar({
        open: true,
        message: "Password changed successfully!",
        severity: "success",
      });
      actions.resetForm();
    } catch(error: any) {
      setSnackbar({
        open: true,
        message: error?.message || "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  return (
    <div>
      <div className={`max-w-full ${isMobile ? 'p-2' : 'p-4'} rounded-xl border border-gray-200`}>
        <Typography variant="h5" className="text-left font-bold text-gray-700">
          Change Password
        </Typography>
        <div>&nbsp;</div>
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, values }) => (
            <Form className={`space-y-${isMobile ? '4' : '5'}`}>
              <PasswordField
                name="currentPassword"
                label="Current Password"
                show={showPassword.current}
                onToggle={() => toggleVisibility("current")}
                error={Boolean(touched.currentPassword && errors.currentPassword)}
                touched={touched.currentPassword && errors.currentPassword}
              />

              <PasswordField
                name="newPassword"
                label="New Password"
                show={showPassword.new}
                onToggle={() => toggleVisibility("new")}
                error={Boolean(touched.newPassword && errors.newPassword)}
                touched={touched.newPassword && errors.newPassword}
              />

              <PasswordField
                name="confirmPassword"
                label="Confirm Password"
                show={showPassword.confirm}
                onToggle={() => toggleVisibility("confirm")}
                error={Boolean(
                  touched.confirmPassword && errors.confirmPassword
                )}
                touched={touched.confirmPassword && errors.confirmPassword}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth={isMobile}
                size="large"
                disabled={isSubmitting}
                sx={{ borderRadius: 2, fontWeight: 600 }}
                startIcon={<LockResetIcon />}
              >
                {isSubmitting ? "Updating..." : "Change Password"}
              </Button>
            </Form>
          )}
        </Formik>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BankerChangePassword;